// Locale resolution + persistence for the language switcher (#i18n).
//
// The saved → tenant → marketing-host → browser → nb resolution below runs synchronously
// when the i18n instance is created (see ./index.ts), so the first
// paint is already in the final language — the instance is never
// mutated from 'nb' to the resolved locale after the fact. Unit tests
// pin the locale to 'nb' in the vitest/browser setup files for
// determinism. Applying a freshly-fetched brand locale to the live app
// lives in ./tenantLocale — this module must not import the i18n
// instance back, since ./index.ts calls resolveInitialLocale() while
// creating it.

import { isMarketingHost } from '@/utils/platformHost'

export const SUPPORTED_LOCALES = ['nb', 'en'] as const
export type AppLocale = (typeof SUPPORTED_LOCALES)[number]

const STORAGE_KEY = 'stemne.locale'

// The tenant's brand locale as seen on the last successful
// /api/public/tenant fetch. localStorage is origin-scoped, so on
// {slug}.stemne.no this is inherently per-tenant. It exists so a repeat
// visitor's FIRST paint is already in the tenant's language — without
// it, every boot would start from the browser language and flip when
// the tenant fetch lands.
const TENANT_LOCALE_KEY = 'stemne.tenantLocale'

export function isAppLocale(value: unknown): value is AppLocale {
  return value === 'nb' || value === 'en'
}

// Map an app locale ("nb"/"en") to the BCP-47 tag that Intl formatters
// (DateTimeFormat / NumberFormat) expect, so date, weekday, and number
// formatting follows the chosen language instead of hardcoding 'nb-NO' at
// every call site. English uses en-GB for day-first date ordering, matching
// the Norwegian/European convention the product is built around.
export function intlLocale(locale: string): string {
  return locale.startsWith('en') ? 'en-GB' : 'nb-NO'
}

// The visitor's explicitly-chosen language, or null if they've never
// picked one. localStorage can throw in private mode / when disabled —
// treat any failure as "no saved choice".
export function savedLocale(): AppLocale | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    return isAppLocale(raw) ? raw : null
  } catch {
    return null
  }
}

export function persistLocale(locale: AppLocale): void {
  try {
    localStorage.setItem(STORAGE_KEY, locale)
  } catch {
    // Storage unavailable — the choice still applies for this session,
    // it just won't survive a reload. Not worth surfacing to the user.
  }
}

// tenant_brand.locale arrives as a BCP-47-ish tag ('nb-NO' is the DB
// default, but the value is operator-editable) — collapse it onto the
// two app locales. Unknown or non-string values yield null so callers
// fall through to the browser-language order.
export function normalizeBrandLocale(value: unknown): AppLocale | null {
  if (typeof value !== 'string') return null
  const tag = value.toLowerCase()
  for (const nb of ['nb', 'no', 'nn']) {
    if (tag === nb || tag.startsWith(`${nb}-`)) return 'nb'
  }
  if (tag === 'en' || tag.startsWith('en-')) return 'en'
  return null
}

export function cachedTenantLocale(): AppLocale | null {
  try {
    const raw = localStorage.getItem(TENANT_LOCALE_KEY)
    return isAppLocale(raw) ? raw : null
  } catch {
    return null
  }
}

export function rememberTenantLocale(locale: AppLocale): void {
  try {
    localStorage.setItem(TENANT_LOCALE_KEY, locale)
  } catch {
    // Same policy as persistLocale: a failed write only costs the
    // tenant-language first paint on the next visit.
  }
}

// Operator-facing path prefixes on a tenant subdomain. The tenant's
// brand locale must not override the operator's own language in the
// dashboard — only end-customer surfaces (booking flow, portal, manage
// links) default to the tenant's language.
const OPERATOR_PATH_PREFIXES = [
  '/dashboard',
  '/admin',
  '/setup',
  '/staff',
  '/auth',
  '/dpa-accept',
  '/dpa-required',
  '/abonnement',
]

// A surface where the visitor is the operator's END CUSTOMER: a tenant
// host (anything that isn't the marketing apex) on a non-operator path.
export function isPublicTenantSurface(
  host: string = currentHost(),
  path: string = currentPath(),
): boolean {
  if (!host || isMarketingHost(host)) return false
  return !OPERATOR_PATH_PREFIXES.some((p) => path === p || path.startsWith(`${p}/`))
}

// Default language for a first-time visitor: a validated locale carried
// by the cross-origin signup transfer wins and is persisted locally. A
// saved choice then wins; on end-customer tenant surfaces the tenant's remembered brand
// language comes next (a Norwegian therapist's booking page should not
// flip to English chrome for an en-US browser). The marketing host
// (stemne.no apex, its www, and bare localhost in dev) then defaults to
// Norwegian without consulting the browser: our search ads are Norwegian,
// and many Norwegians run English browsers and phones (as, likely, does
// Google's AdsBot), so the browser language turned a Norwegian ad into an
// English landing page and cost us Google Ads' landing-page-experience
// rating. A visitor who wants English picks it with the toggle, and that
// saved choice wins on the next load. Everywhere else (tenant operator
// surfaces, tenant pages with no remembered locale) the browser UI
// language decides (any `en*` variant → English), else Norwegian. An
// existing Norwegian visitor is never flipped: no saved choice + a
// non-English browser both resolve to 'nb'.
export function resolveInitialLocale(
  host: string = currentHost(),
  path: string = currentPath(),
  search: string = currentSearch(),
): AppLocale {
  const transferred = transferredLocale(path, search)
  if (transferred) {
    persistLocale(transferred)
    return transferred
  }
  const saved = savedLocale()
  if (saved) return saved
  if (isPublicTenantSurface(host, path)) {
    const tenantDefault = cachedTenantLocale()
    if (tenantDefault) return tenantDefault
  }
  if (isMarketingHost(host)) return 'nb'
  const nav = typeof navigator !== 'undefined' ? navigator.language.toLowerCase() : ''
  if (nav.startsWith('en')) return 'en'
  return 'nb'
}

function transferredLocale(path: string, search: string): AppLocale | null {
  if (path !== '/auth/transfer') return null
  const locale = new URLSearchParams(search).get('locale')
  return isAppLocale(locale) ? locale : null
}

function currentHost(): string {
  return typeof window !== 'undefined' ? (window.location?.hostname ?? '') : ''
}

function currentPath(): string {
  return typeof window !== 'undefined' ? (window.location?.pathname ?? '/') : '/'
}

function currentSearch(): string {
  return typeof window !== 'undefined' ? (window.location?.search ?? '') : ''
}
